import uiI18n from '../i18n';
import { apiFetch } from "../Services/apiClient";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import {
  LuBuilding2,
  LuHousePlus,
  LuRefreshCw,
  LuTriangleAlert,
  LuUmbrella,
} from "react-icons/lu";

import {
  getMyAdsService,
  getMyAdsVacationService,
} from "../Services/myAdsService";

import ListBiens from "../components/ListBiens";
import ListVacation from "../components/ListVacation";
import Spinner from "../components/Spinner";
import { variables } from "../Variables.jsx";

const AdsList = ({ uid }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = getAuth();

  const [ads, setAds] = useState([]);
  const [vacationList, setVacationList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState("");

  const currentUserId =
    uid || auth.currentUser?.uid || null;

  const totalAds = useMemo(
    () => ads.length + vacationList.length,
    [ads.length, vacationList.length]
  );

  const fetchBiens = useCallback(async () => {
    if (!currentUserId) {
      setAds([]);
      setVacationList([]);
      setLoading(false);
      setError(
        t(
          "profile.user_not_connected",
          "Utilisateur non connecté."
        )
      );
      return;
    }

    setLoading(true);
    setError("");

    try {
      const [adsResult, vacationsResult] =
        await Promise.allSettled([
          getMyAdsService(currentUserId),
          getMyAdsVacationService(currentUserId),
        ]);

      if (adsResult.status === "fulfilled") {
        setAds(
          Array.isArray(adsResult.value)
            ? adsResult.value
            : []
        );
      } else {
        setAds([]);
      }

      if (vacationsResult.status === "fulfilled") {
        setVacationList(
          Array.isArray(vacationsResult.value)
            ? vacationsResult.value
            : []
        );
      } else {
        setVacationList([]);
      }

      if (
        adsResult.status === "rejected" &&
        vacationsResult.status === "rejected"
      ) {
        throw new Error(
          adsResult.reason?.message ||
            vacationsResult.reason?.message ||
            uiI18n.t("ui.unable_to_load_your_listings")
        );
      }
    } catch (requestError) {
      setError(
        requestError?.message ||
          t(
            "profile.loading_error",
            "Impossible de charger vos annonces."
          )
      );
    } finally {
      setLoading(false);
    }
  }, [currentUserId, t]);

  useEffect(() => {
    fetchBiens();
  }, [fetchBiens]);

  const deleteListing = async ({
    listing,
    endpoint,
    successMessage,
  }) => {
    const confirmed = window.confirm(
      t(
        "profile.confirm_delete",
        "Êtes-vous sûr de vouloir supprimer cette annonce ?"
      )
    );

    if (!confirmed) {
      return;
    }

    const listingId =
      listing.id || listing.ID || endpoint;

    setDeletingId(listingId);

    try {
      const response = await apiFetch(
        `${variables.API_URL}${endpoint}`,
        {
          method: "DELETE",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(listing),
        }
      );

      if (!response.ok) {
        let result = null;

        try {
          result = await response.json();
        } catch {
          result = null;
        }

        throw new Error(
          result?.message ||
            t(
              "profile.echec",
              "La suppression a échoué."
            )
        );
      }

      toast.success(
        successMessage ||
          t(
            "profile.succes",
            "Annonce supprimée avec succès."
          )
      );

      await fetchBiens();
    } catch (requestError) {
      toast.error(
        requestError?.message ||
          t(
            "profile.delete_error",
            "Erreur lors de la suppression."
          )
      );
    } finally {
      setDeletingId(null);
    }
  };

  const onDelete = (bien) => {
    deleteListing({
      listing: bien,
      endpoint: "Biens/Delete",
    });
  };

  const onDeleteVacation = (bien) => {
    deleteListing({
      listing: bien,
      endpoint: "Vacation/Delete",
    });
  };

  const onEdit = (bien) => {
    navigate(`/EditListing/${bien.id}`);
  };

  const onEditVacation = (bien) => {
    navigate(`/EditVacation/${bien.id}`);
  };

  if (loading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <section className="min-h-[420px]">
      {/* En-tête */}
      <div className="mb-8 flex flex-col gap-4 rounded-3xl border border-slate-100 bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:p-6">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-green-600">
            {t(
              "profile.my_space",
              "Mon espace immobilier"
            )}
          </p>

          <h1 className="mt-2 text-2xl font-black text-slate-950 sm:text-3xl">
            {t(
              "profile.my_annonce",
              "Mes annonces"
            )}
          </h1>

          <p className="mt-2 text-sm text-slate-500">
            {totalAds > 0
              ? `${totalAds} ${t(
                  totalAds > 1
                    ? "profile.published_ads"
                    : "profile.published_ad",
                  totalAds > 1
                    ? "annonces publiées"
                    : "annonce publiée"
                )}`
              : t(
                  "profile.no_published_ads",
                  "Vous n’avez encore publié aucune annonce."
                )}
          </p>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={fetchBiens}
            className="
              inline-flex
              h-11
              items-center
              justify-center
              gap-2
              rounded-xl
              border
              border-slate-200
              bg-white
              px-4
              text-sm
              font-bold
              text-slate-600
              transition
              hover:border-slate-300
              hover:bg-slate-100
              hover:text-slate-900
              focus:outline-none
              focus:ring-4
              focus:ring-slate-500/10
            "
          >
            <LuRefreshCw size={17} />

            {t(
              "profile.refresh",
              "Actualiser"
            )}
          </button>

          <button
            type="button"
            onClick={() => navigate("/CreateBien")}
            className="
              inline-flex
              h-11
              items-center
              justify-center
              gap-2
              rounded-xl
              bg-green-600
              px-5
              text-sm
              font-extrabold
              text-white
              shadow-lg
              shadow-green-600/20
              transition
              hover:-translate-y-0.5
              hover:bg-green-700
              focus:outline-none
              focus:ring-4
              focus:ring-green-500/20
            "
          >
            <LuHousePlus size={18} />

            {t(
              "add_ad",
              "Ajouter une annonce"
            )}
          </button>
        </div>
      </div>

      {/* Erreur */}
      {error && (
        <div
          role="alert"
          className="mb-6 flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
        >
          <LuTriangleAlert
            size={19}
            className="mt-0.5 shrink-0"
          />

          <span>{error}</span>
        </div>
      )}

      {/* État vide */}
      {!error && totalAds === 0 && (
        <div className="rounded-3xl border border-dashed border-slate-300 bg-white px-5 py-16 text-center shadow-sm">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-green-50 text-green-600">
            <LuBuilding2 size={30} />
          </span>

          <h2 className="mt-5 text-xl font-black text-slate-900">
            {t(
              "listing.noData",
              "Aucune annonce disponible"
            )}
          </h2>

          <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-500">
            {t(
              "profile.empty_ads_description",
              "Publiez votre premier bien immobilier ou votre première location de vacances."
            )}
          </p>

          <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() =>
                navigate("/CreateBien")
              }
              className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-green-600 px-5 text-sm font-bold text-white transition hover:bg-green-700"
            >
              <LuHousePlus size={17} />

              {t(
                "create_ad",
                "Publier un bien"
              )}
            </button>

            <button
              type="button"
              onClick={() =>
                navigate("/CreateVacation")
              }
              className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 text-sm font-bold text-slate-700 transition hover:bg-slate-100"
            >
              <LuUmbrella size={17} />

              {t(
                "create_adW",
                "Publier une location de vacances"
              )}
            </button>
          </div>
        </div>
      )}

      {/* Locations de vacances */}
      {vacationList.length > 0 && (
        <div className="mb-10">
          <div className="mb-5 flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-sky-50 text-sky-600">
              <LuUmbrella size={21} />
            </span>

            <div>
              <h2 className="text-xl font-black text-slate-900">
                {t(
                  "profile.vacation_ads",
                  "Locations de vacances"
                )}
              </h2>

              <p className="text-sm text-slate-500">
                {vacationList.length}{" "}
                {t(
                  vacationList.length > 1
                    ? "profile.ads"
                    : "profile.ad",
                  vacationList.length > 1
                    ? "annonces"
                    : "annonce"
                )}
              </p>
            </div>
          </div>

          <ul className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {vacationList.map((bien) => {
              const listingId =
                bien.id || bien.ID;

              return (
                <li
                  key={listingId}
                  className={
                    deletingId === listingId
                      ? "pointer-events-none opacity-50"
                      : ""
                  }
                >
                  <ListVacation
                    stVacation
                    listing={bien}
                    onDelete={() =>
                      onDeleteVacation(bien)
                    }
                    onEdit={() =>
                      onEditVacation(bien)
                    }
                  />
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Biens classiques */}
      {ads.length > 0 && (
        <div>
          <div className="mb-5 flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-green-50 text-green-600">
              <LuBuilding2 size={21} />
            </span>

            <div>
              <h2 className="text-xl font-black text-slate-900">
                {t(
                  "profile.property_ads",
                  "Biens immobiliers"
                )}
              </h2>

              <p className="text-sm text-slate-500">
                {ads.length}{" "}
                {t(
                  ads.length > 1
                    ? "profile.ads"
                    : "profile.ad",
                  ads.length > 1
                    ? "annonces"
                    : "annonce"
                )}
              </p>
            </div>
          </div>

          <ul className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {ads.map((bien) => {
              const listingId =
                bien.id || bien.ID;

              return (
                <li
                  key={listingId}
                  className={
                    deletingId === listingId
                      ? "pointer-events-none opacity-50"
                      : ""
                  }
                >
                  <ListBiens
                    listing={bien}
                    onDelete={() =>
                      onDelete(bien)
                    }
                    onEdit={() =>
                      onEdit(bien)
                    }
                  />
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
};

export default AdsList;